import React from 'react';

import castArray from 'lodash/castArray';

import { useClassnames } from '@lumapps/classnames';
import {
    type MultipleSelectTextFieldProps,
    SelectTextField,
    SelectTextFieldProps,
    type SingleSelectTextFieldProps,
} from '@lumapps/combobox/components/SelectTextField';
import { useDataAttributes } from '@lumapps/data-attributes';
import type { Instance } from '@lumapps/instance/types';
import { useQueryMetadataByIds } from '@lumapps/metadata/hooks/useQueryMetadataByIds';
import { MetadataEntity, type Metadata } from '@lumapps/metadata/types';

import { useMetadataPicker } from '../../hooks/useMetadataPicker';
import type { PickerMetaData } from '../../types';

type InheritedSelectTextFieldProps =
    | 'label'
    | 'placeholder'
    | 'helper'
    | 'error'
    | 'aria-disabled'
    | 'isDisabled'
    | 'isRequired'
    | 'className'
    | 'onChange'
    | 'renderOption'
    | 'icon'
    | 'onSearch'
    | 'onBlur'
    | 'listProps'
    | 'inputRef'
    | 'theme'
    | 'scope'
    | 'labelProps';

interface BaseMetadataPickerFieldProps {
    /**
     * The id of the family of metadata to fetch.
     */
    familyId: Metadata['familyKey'];
    /**
     * The instance on which the metadata should be fetched.
     */
    instanceId?: Instance['id'] | Instance['id'][];
    /**
     * use instance metadata instead
     */
    useInstanceMetadata?: boolean;
    /**
     * Show multiple levels of metadata or only the root metadata in family
     */
    showMultipleLevels?: boolean;
    /**
     * Optional getOptionId
     */
    getOptionId?: SelectTextFieldProps<PickerMetaData>['getOptionId'];
    /**
     * Filter metadata by entity type. Defaults to all entity types.
     */
    canBeAppliedTo?: MetadataEntity[];
    /**
     * Restrict the selectable values to these exact metadata ids. Undefined means no restriction.
     */
    allowedMetadataIds?: string[];
}

export type SingleMetadataPickerFieldProps = Pick<
    SingleSelectTextFieldProps<PickerMetaData>,
    InheritedSelectTextFieldProps
> & {
    selectionType: SingleSelectTextFieldProps['selectionType'];
    /** Currently selected metadata */
    value?: PickerMetaData | string;
};

export type MultipleMetadataPickerFieldProps = Pick<
    MultipleSelectTextFieldProps<PickerMetaData>,
    InheritedSelectTextFieldProps
> & {
    selectionType: MultipleSelectTextFieldProps['selectionType'];
    /** Currently selected metadata */
    value?: Array<PickerMetaData | string>;
};

export type MetadataPickerFieldProps = (SingleMetadataPickerFieldProps | MultipleMetadataPickerFieldProps) &
    BaseMetadataPickerFieldProps;

const CLASSNAME = 'lumx-metadata-picker-field';

const SCOPE = 'metadata-picker';

/**
 * Metadata select text field
 *
 * @family Pickers
 */
export const MetadataPickerField = (props: MetadataPickerFieldProps) => {
    const {
        familyId,
        instanceId,
        useInstanceMetadata,
        canBeAppliedTo = [MetadataEntity.all],
        showMultipleLevels,
        allowedMetadataIds,
        scope = SCOPE,
        getOptionId = 'id',
        listProps,
        className,
        error,
        value,
        ...forwardedProps
    } = props;
    const { get } = useDataAttributes(scope);
    const { block } = useClassnames(CLASSNAME);

    const isMultiple = forwardedProps.selectionType === 'multiple';
    const { status, filteredChoices, getValueName, getMetadataName, onSearch, onOpen } = useMetadataPicker({
        params: useInstanceMetadata
            ? { source: 'instance', instance: instanceId, familyId, canBeAppliedTo }
            : { source: 'customer', instance: instanceId, familyId, canBeAppliedTo },
        showMultipleLevels,
        allowedMetadataIds,
    });

    // Fetching selected metadata if needed
    const valueArray = castArray(value).filter(Boolean);
    const queryMetadata = useQueryMetadataByIds(valueArray, { fields: ['id', 'name'] });

    // Adapt props based on the selection type
    const selectionProps =
        forwardedProps.selectionType === 'single'
            ? {
                  value: queryMetadata.items?.[0] as PickerMetaData,
                  ...forwardedProps,
              }
            : {
                  value: queryMetadata.items?.filter(Boolean) as PickerMetaData[],
                  ...forwardedProps,
              };

    return (
        <SelectTextField<PickerMetaData>
            className={block([className])}
            {...get({ element: isMultiple ? 'multiple-select' : 'select' })}
            {...selectionProps}
            listProps={{
                ...listProps,
                ...get({ element: isMultiple ? 'multiple-option-list' : 'option-list' }),
            }}
            isDisabled={forwardedProps.isDisabled || queryMetadata.isLoading}
            status={status}
            options={filteredChoices}
            getOptionId={getOptionId}
            getOptionName={getMetadataName}
            onSearch={onSearch}
            autoFilter={false}
            error={error}
            hasError={!!error}
            onOpen={onOpen}
            hasClearButton
            showEmptyState
            showErrorState
            openOnFocus
            renderOption={(metadata) => <SelectTextField.Option>{getValueName(metadata)}</SelectTextField.Option>}
        />
    );
};
